"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.generateVerifierAndChallenge = exports.dec2hex = void 0;
/**
 * Converts a decimal number to a hexadecimal string.
 *
 * @param {number} dec - The decimal number to be converted to hexadecimal.
 * @returns {string} The hexadecimal representation of the input decimal number.
 */
const dec2hex = dec => `0${dec.toString(16)}`.slice(-2);

/**
 * Generates a random code verifier for PKCE (Proof Key for Code Exchange) authentication.
 *
 * @returns {string} A random code verifier represented as a hexadecimal string.
 */
exports.dec2hex = dec2hex;
const generateCodeVerifier = () => {
  const array = new Uint32Array(56 / 2);
  window.crypto.getRandomValues(array);
  return Array.from(array, dec2hex).join('');
};

/**
 * Generates an SHA-256 hash of a string.
 *
 * @see https://developer.mozilla.org/en-US/docs/Web/API/SubtleCrypto/digest
 *
 * @param {string} plain - The string to be hashed.
 */
const sha256 = async plain => {
  const encoder = new TextEncoder();
  const data = encoder.encode(plain);
  return window.crypto.subtle.digest('SHA-256', data);
};

/**
 * Base64url encoding is similar to base64 encoding except it uses non-reserved URL characters
 * (e.g., '-', '_', '~') instead of reserved URL characters (e.g., '+', '/', '=').
 *
 * @param {ArrayBuffer} a - The array buffer to be encoded.
 * @returns {string} The base64url encoded string.
 */
const base64urlEncode = a => {
  const bytes = new Uint8Array(a);
  const len = bytes.byteLength;
  let str = '';
  for (let i = 0; i < len; i++) {
    const point = bytes[i];
    if (point !== undefined) {
      str += String.fromCodePoint(point);
    }
  }

  // eslint-disable-next-line unicorn/better-regex -- We need not to change the regex
  return btoa(str).replaceAll('+', '-').replaceAll('/', '_').replace(/[=]+$/, '');
};

/**
 * Generates a code challenge from a code verifier for PKCE (Proof Key for Code Exchange) authentication.
 * The code challenge is a base64url encoded SHA-256 hash of the code verifier.
 *
 * See https://tools.ietf.org/html/rfc7636#section-4.2 for more details.
 *
 * @param {string} v - The code verifier to generate a code challenge from.
 * @returns {string} The code challenge.
 *
 */
async function generateCodeChallengeFromVerifier(v) {
  const hashed = await sha256(v);
  return base64urlEncode(hashed);
}
/**
 * Generates a code verifier and a code challenge for PKCE (Proof Key for Code Exchange) authentication.
 * The code verifier is a random string and the code challenge is a base64url encoded SHA-256 hash of the code verifier.
 * See https://tools.ietf.org/html/rfc7636#section-4.2 for more details.
 *
 * @returns {VerifierAndChallenge} The code verifier and the code challenge.
 *
 */
const generateVerifierAndChallenge = async () => {
  const codeVerifier = generateCodeVerifier();
  const codeChallenge = await generateCodeChallengeFromVerifier(codeVerifier);
  return {
    codeChallenge,
    codeVerifier
  };
};
exports.generateVerifierAndChallenge = generateVerifierAndChallenge;
